import React from "react";
import { Space } from "antd";
import { AxTree, AxDivider, AxButton } from "@dynata/axiom3";
const treeData = [
  {
    title: "parent 1",
    key: "0-0",
    children: [
      {
        title: "parent 1-0",
        key: "0-0-0",

        children: [
          {
            title: "leaf",
            key: "0-0-0-0",
          },
          {
            title: "leaf",
            key: "0-0-0-1",
          },
        ],
      },
      {
        title: "parent 1-1",
        key: "0-0-1",
        children: [
          {
            title: "sss",

            key: "0-0-1-0",
          },
        ],
      },
    ],
  },
];

class Tree extends React.Component {
  render() {
    return (
      <div className="section" id="tree">
        <h3>Tree</h3>
        <p>A hierarchical list structure component.</p>
        <AxDivider orientation="left" plain>
          Basic Usage
        </AxDivider>
        <Space direction="vertical">
          <Space size="large" direction="vertical">
            <pre>{` <AxTree treeData={treeData} />`}</pre>
            <div className="prop-header">
              <p className="props-title">Props</p>
              <p>
                For AntD props, visit{" "}
                <AxButton
                  size="small"
                  type="link"
                  target="_blank"
                  href="https://ant.design/components/tree/#API"
                >
                  https://ant.design/components/tree/#API
                </AxButton>
              </p>
            </div>

            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>allowDrop</td>
                  <td>Whether to allow dropping on the node</td>
                  <td>
                    <pre>{`({(dropNode, dropPosition)}) =&gt;`}</pre> boolean
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>autoExpandParent</td>
                  <td>Whether to automatically expand a parent treeNode</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>blockNode</td>
                  <td>Whether treeNode fill remaining horizontal space</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>checkable</td>
                  <td>Add a Checkbox before the treeNodes</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>checkedKeys</td>
                  <td>
                    (Controlled) Specifies the keys of the checked treeNodes
                    (PS: When this specifies the key of a treeNode which is also
                    a parent treeNode, all the children treeNodes of will be
                    checked; and vice versa, when it specifies the key of a
                    treeNode which is a child treeNode, its parent treeNode will
                    also be checked. When <code>checkable</code> and{" "}
                    <code>checkStrictly</code> is true, its object has{" "}
                    <code>checked</code> and <code>halfChecked</code> property.
                    Regardless of whether the child or parent treeNode is
                    checked, they won't impact each other
                  </td>
                  <td>
                    string[] |{" "}
                    <pre>{`{checked: string[], halfChecked: string[]}`}</pre>
                  </td>
                  <td>[]</td>
                  <td></td>
                </tr>
                <tr>
                  <td>checkStrictly</td>
                  <td>
                    Check treeNode precisely; parent treeNode and children
                    treeNodes are not associated
                  </td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>defaultCheckedKeys</td>
                  <td>Specifies the keys of the default checked treeNodes</td>
                  <td>string[]</td>
                  <td>[]</td>
                  <td></td>
                </tr>
                <tr>
                  <td>defaultExpandAll</td>
                  <td>Whether to expand all treeNodes by default</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>defaultExpandedKeys</td>
                  <td>Specify the keys of the default expanded treeNodes</td>
                  <td>string[]</td>
                  <td>[]</td>
                  <td></td>
                </tr>
                <tr>
                  <td>defaultExpandParent</td>
                  <td>If auto expand parent treeNodes when init</td>
                  <td>boolean</td>
                  <td>true</td>
                  <td></td>
                </tr>
                <tr>
                  <td>defaultSelectedKeys</td>
                  <td>Specifies the keys of the default selected treeNodes</td>
                  <td>string[]</td>
                  <td>[]</td>
                  <td></td>
                </tr>
                <tr>
                  <td>disabled</td>
                  <td>Whether disabled the tree</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>draggable</td>
                  <td>
                    Specifies whether this Tree or the node is draggable. Use{" "}
                    <code>icon: false</code> to disable drag handler icon
                  </td>
                  <td>
                    boolean |{" "}
                    <pre>{`((node: DataNode) =&gt; boolean) | { icon?: React.ReactNode | false, nodeDraggable?: (node: DataNode) =&gt; boolean }`}</pre>
                  </td>
                  <td>false</td>
                  <td>
                    <code>config</code>: 4.17.0
                  </td>
                </tr>
                <tr>
                  <td>expandedKeys</td>
                  <td>
                    (Controlled) Specifies the keys of the expanded treeNodes
                  </td>
                  <td>string[]</td>
                  <td>[]</td>
                  <td></td>
                </tr>
                <tr>
                  <td>fieldNames</td>
                  <td>Customize node title, key, children field name</td>
                  <td>object</td>
                  <td>
                    <pre>{`{ title: <code>title</code>, key: <code>key</code>, children: <code>children</code> }`}</pre>
                  </td>
                  <td>4.17.0</td>
                </tr>
                <tr>
                  <td>filterTreeNode</td>
                  <td>
                    Defines a function to filter (highlight) treeNodes. When the
                    function returns <code>true</code>, the corresponding
                    treeNode will be highlighted
                  </td>
                  <td>function(node)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>height</td>
                  <td>
                    Config virtual scroll height. Will not support horizontal
                    scroll when enable this
                  </td>
                  <td>number</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>icon</td>
                  <td>Customize treeNode icon</td>
                  <td>ReactNode | (props) =&gt; ReactNode</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>loadData</td>
                  <td>Load data asynchronously</td>
                  <td>function(node)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>loadedKeys</td>
                  <td>
                    (Controlled) Set loaded tree nodes. Need work with{" "}
                    <code>loadData</code>
                  </td>
                  <td>string[]</td>
                  <td>[]</td>
                  <td></td>
                </tr>
                <tr>
                  <td>multiple</td>
                  <td>Allows selecting multiple treeNodes</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>rootClassName</td>
                  <td>ClassName on the root element</td>
                  <td>string</td>
                  <td>-</td>
                  <td>4.20.0</td>
                </tr>
                <tr>
                  <td>rootStyle</td>
                  <td>Style on the root element</td>
                  <td>CSSProperties</td>
                  <td>-</td>
                  <td>4.20.0</td>
                </tr>
                <tr>
                  <td>selectable</td>
                  <td>Whether can be selected</td>
                  <td>boolean</td>
                  <td>true</td>
                  <td></td>
                </tr>
                <tr>
                  <td>selectedKeys</td>
                  <td>
                    (Controlled) Specifies the keys of the selected treeNodes
                  </td>
                  <td>string[]</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>showIcon</td>
                  <td>
                    Shows the icon before a TreeNode's title. There is no
                    default style; you must set a custom style for it if set to
                    true
                  </td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>showLine</td>
                  <td>Shows a connecting line</td>
                  <td>
                    boolean |{" "}
                    <pre>{`{showLeafIcon: boolean | ReactNode | ((props: AntTreeNodeProps) =&gt; ReactNode)}`}</pre>
                  </td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>switcherIcon</td>
                  <td>Customize collapse/expand icon of tree node</td>
                  <td>
                    ReactNode | ((props: AntTreeNodeProps) =&gt; ReactNode)
                  </td>
                  <td>-</td>
                  <td>renderProps: 4.20.0</td>
                </tr>
                <tr>
                  <td>titleRender</td>
                  <td>Customize tree node title render</td>
                  <td>(nodeData) =&gt; ReactNode</td>
                  <td>-</td>
                  <td>4.5.0</td>
                </tr>
                <tr>
                  <td>treeData</td>
                  <td>
                    The treeNodes data Array, if set it then you need not to
                    construct children TreeNode. (key should be unique across
                    the whole array)
                  </td>
                  <td>
                    <pre>{` array&lt;{(key, title, children, [disabled, selectable])}
                    &gt;`}</pre>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>virtual</td>
                  <td>Disable virtual scroll when set to false</td>
                  <td>boolean</td>
                  <td>true</td>
                  <td>4.1.0</td>
                </tr>
                <tr>
                  <td>onCheck</td>
                  <td>Callback function for when the onCheck event occurs</td>
                  <td>
                    <pre>{`function(checkedKeys, e:{checked: bool, checkedNodes, node, event, halfCheckedKeys})`}</pre>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onDragEnd</td>
                  <td>Callback function for when the onDragEnd event occurs</td>
                  <td>
                    <pre>{`function({(event, node)})`}</pre>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onDragEnter</td>
                  <td>
                    Callback function for when the onDragEnter event occurs
                  </td>
                  <td>
                    <pre>{`function({(event, node, expandedKeys)})`}</pre>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onDragLeave</td>
                  <td>
                    Callback function for when the onDragLeave event occurs
                  </td>
                  <td>
                    <pre>{`function({(event, node)})`}</pre>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onDragOver</td>
                  <td>
                    Callback function for when the onDragOver event occurs
                  </td>
                  <td>
                    <pre>{`function({(event, node)})`}</pre>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onDragStart</td>
                  <td>
                    Callback function for when the onDragStart event occurs
                  </td>
                  <td>
                    <pre>{`function({(event, node)})`}</pre>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onDrop</td>
                  <td>Callback function for when the onDrop event occurs</td>
                  <td>
                    <pre>{`function({(event, node, dragNode, dragNodesKeys)})`}</pre>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onExpand</td>
                  <td>
                    Callback function for when a treeNode is expanded or
                    collapsed
                  </td>
                  <td>
                    <pre>{`function(expandedKeys, {expanded: bool, node})`}</pre>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onLoad</td>
                  <td>Callback function for when a treeNode is loaded</td>
                  <td>
                    <pre>{`function(loadedKeys, {(event, node)})`}</pre>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onRightClick</td>
                  <td>
                    Callback function for when the user right clicks a treeNode
                  </td>
                  <td>
                    <pre>{`function({(event, node)})`} </pre>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onSelect</td>
                  <td>Callback function for when the user clicks a treeNode</td>
                  <td>
                    <pre>{`function(selectedKeys, e:{selected: bool, selectedNodes, node, event})`}</pre>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>...others</td>
                  <td>
                    All extra props are passed down to the{" "}
                    <>
                      <AxButton
                        size="small"
                        type="link"
                        target="_blank"
                        href="https://4x.ant.design/components/tree/#API"
                      >
                        {`<Tree />`}
                      </AxButton>
                    </>{" "}
                    component.
                  </td>
                  <td></td>
                  <td>-</td>
                  <td></td>
                </tr>
              </tbody>
            </table>

            <div className="prop-header">
              <p className="props-title">Props: TreeNode</p>
            </div>
            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>checkable</td>
                  <td>
                    When Tree is checkable, set TreeNode display Checkbox or not
                  </td>
                  <td>boolean</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>disableCheckbox</td>
                  <td>Disables the checkbox of the treeNode</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>disabled</td>
                  <td>Disables the treeNode</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>icon</td>
                  <td>
                    Customize icon. When you pass component, whose render will
                    receive full TreeNode props as component props
                  </td>
                  <td>ReactNode | (props) =&gt; ReactNode</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>isLeaf</td>
                  <td>
                    Determines if this is a leaf node(effective when{" "}
                    <code>loadData</code> is specified). <code>false</code> will
                    force trade TreeNode as a parent node
                  </td>
                  <td>boolean</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>key</td>
                  <td>
                    Used with (default)ExpandedKeys / (default)CheckedKeys /
                    (default)SelectedKeys. P.S.: It must be unique in all of
                    treeNodes of the tree
                  </td>
                  <td>string</td>
                  <td>(internal calculated position of treeNode)</td>
                  <td></td>
                </tr>
                <tr>
                  <td>selectable</td>
                  <td>Set whether the treeNode can be selected</td>
                  <td>boolean</td>
                  <td>true</td>
                  <td></td>
                </tr>
                <tr>
                  <td>title</td>
                  <td>Title</td>
                  <td>ReactNode</td>
                  <td>
                    <code>---</code>
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
            <div className="prop-header">
              <p className="props-title">Props: DirectoryTree</p>
            </div>
            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>expandAction</td>
                  <td>
                    Directory open logic, optional: false | <code>click</code> |{" "}
                    <code>doubleClick</code>
                  </td>
                  <td>string | boolean</td>
                  <td>
                    <code>click</code>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="prop-header">
              <p className="props-title">Methods</p>
            </div>

            <table className="props-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <pre>{`scrollTo({ key: string | number; align?: 'top' | 'bottom' | 'auto'; offset?: number })`}</pre>
                  </td>
                  <td>Scroll to key item in virtual scroll</td>
                </tr>
              </tbody>
            </table>

            <pre>{`const treeData = [
  {
    title: "parent 1",
    key: "0-0",
    children: [
      {
        title: "parent 1-0",
        key: "0-0-0",

        children: [
          {
            title: "leaf",
            key: "0-0-0-0",
          },
          {
            title: "leaf",
            key: "0-0-0-1",
          },
        ],
      },
      {
        title: "parent 1-1",
        key: "0-0-1",
        children: [
          {
            title: "sss",

            key: "0-0-1-0",
          },
        ],
      },
    ],
  },
];
            
<AxTree
  style={{ width: "300px" }}
  showIcon
  defaultExpandAll
  defaultSelectedKeys={["0-0-0"]}
  treeData={treeData}
/>`}</pre>
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          Default
        </AxDivider>
        <Space>
          <AxTree
            style={{ width: "300px" }}
            showIcon
            defaultExpandAll
            defaultSelectedKeys={["0-0-0"]}
            treeData={treeData}
          />
        </Space>

        <AxDivider orientation="left" plain>
          checkable
        </AxDivider>
        <Space>
          <AxTree
            style={{ width: "300px" }}
            checkable
            defaultExpandAll
            defaultSelectedKeys={["0-0-0"]}
            treeData={treeData}
          />
        </Space>

        <AxDivider orientation="left" plain>
          Treeline
        </AxDivider>
        <Space>
          <AxTree
            style={{ width: "300px" }}
            showLine={true}
            isLeaf={true}
            showLeafIcon={true}
            showIcon={false}
            defaultExpandAll
            defaultSelectedKeys={["0-0-0"]}
            treeData={treeData}
          />
        </Space>
      </div>
    );
  }
}
export default Tree;
